import { useAlgoliaSearchStore } from '@/stores/Category/AlgoliaSearchStore';
import { AlgoliaFilterType } from '@/types/Algolia/AlgoliaFilterType';
import { AlgoliaSortingOption } from '@/types/Algolia/AlgoliaSortingOption';
import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import _ from 'lodash';
import type { SearchResponse } from '@algolia/client-search';

export const useAlgoliaProductStore = defineStore(
  `algolia-product-${window.store_code}`,
  () => {
    const { t } = useI18n();
    const searchStore = useAlgoliaSearchStore();

    const categoryId = window.category_id;
    const categoryName = window.category_name;

    const productIndex = window.product_index;
    const enableAlgoliaFilters = window.enable_algolia_filters;

    const gridView: Ref<boolean> = ref(true);

    const getAlgoliaFilters = computed(() => {
      return categoryId
        ? `categories.id:"${categoryId}" AND categories.name:"${categoryName}"`
        : `categories.name:"${categoryName}"`;
    });

    const availableFilters: AlgoliaFilterType[] = [
      {
        name: t('pages.category.product_filters.material_name'),
        type: 'checkbox-list',
        filterAttribute: 'material',
        prismicIdentifier: 'material',
      },
      {
        name: t('pages.category.product_filters.tags_audience_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.audience',
        prismicIdentifier: 'audience',
      },
      {
        name: t('pages.category.product_filters.tags_color_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.color',
        prismicIdentifier: 'color',
      },
      {
        name: t('pages.category.product_filters.tags_lifestyle_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.lifestyle',
        prismicIdentifier: 'lifestyle',
      },
      {
        name: t('pages.category.product_filters.tags_theme_name'),
        type: 'themes',
        filterAttribute: 'tags.theme',
        prismicIdentifier: 'theme',
      },
    ];
    const filters: Ref<AlgoliaFilterType[]> = ref([]);

    const sortingOptions: AlgoliaSortingOption[] = [
      {
        value: `${productIndex}_popular_desc`,
        label: t('pages.category.sort.popular'),
        isDefaultOption: true,
      },
      {
        value: `${productIndex}_price_asc`,
        label: t('pages.category.sort.price_asc'),
        isDefaultOption: false,
      },
      {
        value: `${productIndex}_price_desc`,
        label: t('pages.category.sort.price_desc'),
        isDefaultOption: false,
      },
      {
        value: `${productIndex}_name_asc`,
        label: t('pages.category.sort.name_asc'),
        isDefaultOption: false,
      },
      {
        value: `${productIndex}_name_desc`,
        label: t('pages.category.sort.name_desc'),
        isDefaultOption: false,
      },
    ];

    async function retrieveAvailableFilters() {
      if (!enableAlgoliaFilters) {
        return;
      }

      const searchRequestResults = (
        await searchStore.getAlgoliaClient().search({
          requests: [
            {
              indexName: productIndex,
              filters: getAlgoliaFilters.value,
            },
          ],
        })
      ).results;

      const searchResult = searchRequestResults[0] as SearchResponse;

      const facetOrdering =
        searchResult.renderingContent?.facetOrdering?.facets?.order ??
        undefined;

      if (!facetOrdering || _.isEmpty(facetOrdering)) {
        console.debug(
          'No rules set up to display facets. Check Algolia dashboard to configure facet ordering: not displaying filters.'
        );
        return;
      }

      _.each(facetOrdering, (facet: string) => {
        const filter = _.find(
          availableFilters,
          (filter: AlgoliaFilterType) => filter.filterAttribute === facet
        );

        if (filter) {
          filters.value.push(filter);
        }
      });
    }

    return {
      productIndex,
      enableAlgoliaFilters,
      gridView,
      retrieveAvailableFilters,
      availableFilters,
      filters,
      sortingOptions,
    };
  }
);
